import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Loader2, Plus, CheckCircle, CreditCard, FileText, IndianRupee, Users, Trash2, Paperclip } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import CandidateCard from '../../components/CandidateCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const EMPTY_CANDIDATE = {
  name: '', fatherName: '', motherName: '', nukh: '',
  gender: '', dateOfBirth: '', birthTime: '', birthPlace: '', maritalStatus: '',
  height: '', weight: '', complexion: '', bloodGroup: '', mapiOrManglik: '',
  unmarriedBrothers: '0', marriedBrothers: '0', unmarriedSisters: '0', marriedSisters: '0',
  maternalName: '', nukhOfMaternals: '', placeOfMaternals: '',
  education: '', occupation: '', workingAt: '', annualIncome: '',
  phone: '', whatsapp: '', email: '',
  currentAddress: '', currentPlace: '', currentState: '', currentCountry: 'India',
  nativePlace: '', nativeState: '',
  hobbies: '', aboutCandidate: '', partnerPreference: '',
};

const EMPTY_ATTENDEE = { name: '', relation: '', phone: '' };

const PSRegisterPage = () => {
  const navigate = useNavigate();
  const nextClientId = useRef(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [nukhOptions, setNukhOptions] = useState([]);

  // Family-level fields
  const [familyForm, setFamilyForm] = useState({
    email: '', password: '', confirmPassword: '',
    guardianName: '', guardianWhatsapp: '',
    paymentMode: '', paymentDetails: '',
  });

  // Candidates
  const [candidates, setCandidates] = useState([{ ...EMPTY_CANDIDATE, _clientId: '0' }]);
  const [candidateFiles, setCandidateFiles] = useState([{ photo: null }]);
  const [expandedCandidates, setExpandedCandidates] = useState([0]);

  // Attending
  const [attendingInPerson, setAttendingInPerson] = useState(true);
  const [attendees, setAttendees] = useState([
    { ...EMPTY_ATTENDEE, relation: 'Parent / Guardian 1' },
  ]);
  const [attendeeFiles, setAttendeeFiles] = useState([null]);
  const [attendeePreviews, setAttendeePreviews] = useState([null]);

  // Terms
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');

  // Payment proof
  const [paymentProof, setPaymentProof] = useState(null);
  const paymentProofRef = useRef(null);
  const attendeePhotoRefs = useRef([]);

  // Info attachments
  const [infoAttachments, setInfoAttachments] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ps/options`).then(res => {
      setNukhOptions(res.data.nukhs || []);
    }).catch(() => {});
  }, []);

  // Fee calculation (only when attending in person)
  const namedAttendees = attendingInPerson ? attendees.filter(a => a.name.trim()) : [];
  const totalHeads = attendingInPerson ? candidates.length + namedAttendees.length : 0;
  const totalFee = totalHeads * 1000;

  const handleFamilyChange = (field, value) => {
    setFamilyForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleCandidate = (idx) => {
    setExpandedCandidates(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  const addCandidate = () => {
    if (candidates.length >= 3) { toast.error('Maximum 3 candidates allowed'); return; }
    const newId = String(nextClientId.current++);
    setCandidates(prev => [...prev, { ...EMPTY_CANDIDATE, _clientId: newId }]);
    setCandidateFiles(prev => [...prev, { photo: null }]);
    setExpandedCandidates(prev => [...prev, candidates.length]);
  };

  const removeCandidate = (idx) => {
    if (candidates.length <= 1) return;
    setCandidates(prev => prev.filter((_, i) => i !== idx));
    setCandidateFiles(prev => prev.filter((_, i) => i !== idx));
    setExpandedCandidates(prev => prev.filter(i => i !== idx).map(i => i > idx ? i - 1 : i));
  };

  const handleCandidateDataChange = (idx, newData) => {
    setCandidates(prev => {
      const updated = [...prev];
      updated[idx] = { ...newData, _clientId: prev[idx]._clientId };
      return updated;
    });
  };

  const handleFileChange = (idx, fileType, file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error(`File size is ${(file.size / (1024 * 1024)).toFixed(1)}MB. Please upload a photo less than 5MB.`);
      return;
    }
    if (file && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG or WEBP image.');
      return;
    }
    setCandidateFiles(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [fileType]: file };
      return updated;
    });
  };

  const handleAttendeeChange = (idx, field, value) => {
    setAttendees(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleAttendeePhoto = (idx, file) => {
    if (!file) return;
    setAttendeeFiles(prev => { const u = [...prev]; u[idx] = file; return u; });
    setAttendeePreviews(prev => { const u = [...prev]; u[idx] = URL.createObjectURL(file); return u; });
  };

  const removeAttendeePhoto = (idx) => {
    setAttendeeFiles(prev => { const u = [...prev]; u[idx] = null; return u; });
    setAttendeePreviews(prev => { const u = [...prev]; u[idx] = null; return u; });
  };

  const addAttendee = () => {
    if (attendees.length >= 3) return;
    const labels = ['Parent / Guardian 1', 'Parent / Guardian 2', 'Family Member / Attendee'];
    setAttendees(prev => [...prev, { ...EMPTY_ATTENDEE, relation: labels[prev.length] || 'Attendee' }]);
    setAttendeeFiles(prev => [...prev, null]);
    setAttendeePreviews(prev => [...prev, null]);
  };

  const removeAttendee = (idx) => {
    if (attendees.length <= 1) return;
    setAttendees(prev => prev.filter((_, i) => i !== idx));
    setAttendeeFiles(prev => prev.filter((_, i) => i !== idx));
    setAttendeePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    if (!familyForm.email.trim()) { toast.error('Email is required'); return false; }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(familyForm.email.trim())) { toast.error('Invalid email format'); return false; }
    if (!familyForm.password || familyForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return false; }
    if (familyForm.password !== familyForm.confirmPassword) { toast.error('Passwords do not match'); return false; }
    if (!familyForm.guardianName?.trim()) { toast.error('Guardian Name is mandatory'); return false; }
    if (!familyForm.guardianWhatsapp?.trim()) { toast.error('Guardian WhatsApp is mandatory'); return false; }
    if (!/^\d{10}$/.test(familyForm.guardianWhatsapp.trim())) { toast.error('Guardian WhatsApp must be exactly 10 digits'); return false; }

    const required = [
      'name', 'fatherName', 'gender', 'dateOfBirth', 'education', 'phone',
      'currentPlace', 'currentState', 'currentAddress', 'currentCountry',
      'nativePlace', 'nativeState', 'email',
      'maternalName', 'nukhOfMaternals', 'placeOfMaternals',
      'height', 'weight', 'occupation', 'workingAt',
    ];
    const fieldLabels = {
      name: 'Full Name', fatherName: "Father's Name", gender: 'Gender', dateOfBirth: 'Date of Birth',
      education: 'Education', phone: 'Phone', currentPlace: 'Place', currentState: 'State',
      currentAddress: 'Address', currentCountry: 'Country', nativePlace: 'Native Place', nativeState: 'Native State',
      email: 'Email', unmarriedBrothers: 'Unmarried Brothers', marriedBrothers: 'Married Brothers',
      unmarriedSisters: 'Unmarried Sisters', marriedSisters: 'Married Sisters',
      maternalName: 'Maternal Name', nukhOfMaternals: 'Nukh of Maternals', placeOfMaternals: 'Place of Maternals',
      height: 'Height', weight: 'Weight', complexion: 'Complexion', occupation: 'Occupation', workingAt: 'Working At',
    };
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      for (const f of required) {
        if (!String(c[f] || '').trim()) {
          toast.error(`Please fill "${fieldLabels[f] || f}" for Candidate ${i + 1}`);
          setExpandedCandidates(prev => prev.includes(i) ? prev : [...prev, i]);
          return false;
        }
      }
      if (!/^\d{10}$/.test(c.phone?.trim())) { toast.error(`Phone must be 10 digits for Candidate ${i + 1}`); return false; }
      if (!c.whatsapp?.trim()) { toast.error(`WhatsApp is mandatory for Candidate ${i + 1}`); return false; }
      if (!/^\d{10}$/.test(c.whatsapp?.trim())) { toast.error(`WhatsApp must be 10 digits for Candidate ${i + 1}`); return false; }
      if (!candidateFiles[i]?.photo) { toast.error(`Photo is mandatory for Candidate ${i + 1}`); setExpandedCandidates(prev => prev.includes(i) ? prev : [...prev, i]); return false; }
    }

    if (attendingInPerson) {
      const namedAtts = attendees.filter(a => a.name.trim());
      if (namedAtts.length === 0) {
        toast.error('At least one attendee is required when attending in-person / व्यक्तिगत उपस्थिति के लिए कम से कम एक सहभागी आवश्यक है');
        return false;
      }
      for (let i = 0; i < namedAtts.length; i++) {
        const a = namedAtts[i];
        if (!a.relation?.trim()) { toast.error(`Relation is required for attendee "${a.name}"`); return false; }
        if (!a.phone?.trim()) { toast.error(`Phone is required for attendee "${a.name}"`); return false; }
        const origIdx = attendees.indexOf(a);
        if (!a.photo && !attendeeFiles[origIdx]) { toast.error(`Photo is mandatory for attendee "${a.name}". It will be used for ID card.`); return false; }
      }
      if (!familyForm.paymentMode) { toast.error('Please select a payment mode'); return false; }
      if (!familyForm.paymentDetails?.trim()) { toast.error('Please enter transaction details'); return false; }
      if (!paymentProof) { toast.error('Payment proof screenshot is mandatory'); return false; }
    }
    if (!termsAccepted) {
      setTermsError(true);
      toast.error('कृपया नियम व शर्तों को स्वीकार करें');
      return false;
    }
    setTermsError(false);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      const cleanCandidates = candidates.map(({ _clientId, ...rest }) => rest);
      const jsonBody = {
        email: familyForm.email,
        password: familyForm.password,
        guardianName: familyForm.guardianName,
        guardianWhatsapp: familyForm.guardianWhatsapp,
        candidates: cleanCandidates,
        attendingInPerson,
        attendees: attendingInPerson ? attendees : [],
        paymentMode: familyForm.paymentMode,
        paymentDetails: familyForm.paymentDetails,
        additionalInfo: additionalInfo.trim(),
      };
      fd.append('formData', JSON.stringify(jsonBody));
      candidateFiles.forEach((files, idx) => {
        if (files.photo) fd.append(`candidate_${idx}_photo`, files.photo);
      });
      if (attendingInPerson) {
        attendeeFiles.forEach((file, idx) => {
          if (file) fd.append(`attendee_${idx}_photo`, file);
        });
      }
      if (paymentProof) fd.append('paymentProof', paymentProof);
      infoAttachments.forEach((file, idx) => {
        fd.append(`info_attachment_${idx}`, file);
      });

      // Retry logic for flaky mobile connections
      const MAX_RETRIES = 3;
      let lastError = null;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const res = await axios.post(`${API}/ps/register`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000, // 2 min timeout for slow mobile uploads
          });
          localStorage.setItem('psToken', res.data.token);
          localStorage.setItem('psUserId', res.data.user.id);
          localStorage.setItem('psUserName', res.data.user.candidates?.[0]?.name || '');
          setRegisteredUser(res.data.user);
          setSubmitted(true);
          toast.success('Registration successful!');
          return; // Success — exit
        } catch (err) {
          lastError = err;
          // Only retry on network/timeout errors, not on validation (4xx) errors
          const isNetworkError = !err.response || err.code === 'ECONNABORTED' || err.message === 'Network Error';
          if (!isNetworkError || attempt === MAX_RETRIES) break;
          // Wait before retrying (exponential backoff: 2s, 4s)
          const delay = Math.pow(2, attempt) * 1000;
          toast.info(`Connection issue, retrying... (${attempt}/${MAX_RETRIES - 1})`);
          await new Promise(r => setTimeout(r, delay));
        }
      }
      // All retries failed or got a server error
      const detail = lastError?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (lastError?.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please check your internet connection and try again.');
      } else {
        toast.error('Cannot connect to server. Please check your internet connection and try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && registeredUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-12">
        <div className="max-w-lg mx-auto px-4">
          <Card className="p-8 text-center" data-testid="ps-reg-success">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-gray-600 mb-4">Your Parichay Sangam 2026 registration has been submitted.</p>
            <div className="bg-gray-50 border rounded-lg p-4 mb-4 text-left space-y-2">
              <p className="text-sm"><span className="text-gray-500">Family Code:</span> <span className="font-bold text-orange-700" data-testid="ps-reg-family-code">{registeredUser.familyCode}</span></p>
              {registeredUser.candidates?.map((c, i) => (
                <p key={i} className="text-sm">
                  <span className="text-gray-500">Candidate {i+1}:</span>{' '}
                  <span className="font-bold text-orange-700">{c.candidateCode}</span> — {c.name}
                </p>
              ))}
              <p className="text-sm"><span className="text-gray-500">Total Fee:</span> <span className="font-bold">{'\u20B9'}{registeredUser.paymentAmount?.toLocaleString()}</span></p>
              <p className="text-sm"><span className="text-gray-500">Login Email:</span> <span className="font-medium">{registeredUser.email}</span></p>
            </div>
            <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded mb-6">
              Your registration is pending admin approval. You can login anytime to view/edit your details.
            </p>
            <Button onClick={() => navigate('/parichay-sangam/dashboard')} className="bg-orange-600 hover:bg-orange-700" data-testid="ps-goto-dashboard">
              Go to Dashboard
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <section className="bg-gradient-to-r from-orange-500 to-amber-500">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-6">
          <img
            src="https://customer-assets.emergentagent.com/job_eb6102e5-96eb-40a4-9fc1-e76d00d027fa/artifacts/osywbjv5_Parichay%20Sangam%202026%20logo.jpeg"
            alt="Parichay Sangam 2026"
            className="h-28 md:h-36 w-auto object-contain py-2"
          />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Parichay Sangam 2026 — Registration</h1>
            <p className="text-white/80 mt-1">{'\u092A\u0930\u093F\u091A\u092F \u0938\u0902\u0917\u092E 2026 — \u092A\u0902\u091C\u0940\u0915\u0930\u0923'}</p>
          </div>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 py-8">
        {/* Admin Notice */}
        <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300 mb-4 text-sm" data-testid="ps-announcement-banner">
          <p className="font-bold text-blue-900 text-base mb-1">Admin Registration Form</p>
          <p className="text-blue-800">This form is for admin use only. Public registrations are closed. Both Male and Female candidates can be registered.</p>
        </div>

        <div className="bg-red-50 p-5 rounded-lg border-2 border-red-300 mb-4 text-sm" data-testid="ps-important-notes">
          <p className="font-bold text-red-800 text-base mb-3">कृपया ध्यान दें</p>
          <ul className="space-y-3 text-red-900 leading-relaxed list-disc pl-5">
            <li>फॉर्म भरना शुरु करने से पहले यह निश्चित कर लें कि आपके परिवार के कितने उम्मीदवारों का पंजीकरण करना है। एक परिवार यानी उन्हीं माता-पिता की तीन संतानों (योग्य उम्मीदवारों) तक के लिये एक ही फॉर्म भरना है। फोटो भी अपलोड करने हैं। अतः पहले उम्मीदवारों की संख्या तय करें।</li>
            <li>कार्यक्रम में भाग लेने वालों के नाम भी ध्यान में रखें। माता, पिता या संरक्षक व एक अन्य परिजन कार्यक्रम में भाग ले सकेंगे। अतः इनके नाम तय करें। इनके फोटो भी अपलोड करने हैं।</li>
            <li className="font-semibold">उम्मीदवार / उम्मीदवारों के साथ माता, पिता या संरक्षक में से कम से कम किसी एक का कार्यक्रम में आना आवश्यक है।</li>
          </ul>
        </div>

        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4 text-sm">
          <p className="font-semibold text-amber-800">Note / नोट:</p>
          <ul className="list-disc ml-5 text-amber-700 mt-1 space-y-1">
            <li>You can register up to <strong>3 candidates</strong> in one form / एक फॉर्म में अधिकतम 3 उम्मीदवार</li>
            <li>Fee: <strong>{'\u20B9'}1,000 per head</strong> (candidates + attendees) / शुल्क: {'\u20B9'}1,000 प्रति व्यक्ति</li>
            <li>All photos (candidate, attendee, payment proof) must be <strong>JPG, PNG or WEBP</strong> format, max <strong>5MB</strong> per file / सभी फोटो JPG, PNG या WEBP, अधिकतम 5MB</li>
            <li>Attendee photo is <strong>mandatory</strong> and will be used for ID Card generation / सहभागी फोटो अनिवार्य है, ID Card के लिए उपयोग होगा</li>
            <li>Fields marked with <span className="text-destructive font-bold">*</span> are mandatory</li>
            <li>Already registered? <Link to="/parichay-sangam/login" className="text-orange-600 font-medium underline">Login here</Link></li>
          </ul>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-8 text-sm" data-testid="ps-video-tutorials">
          <p className="font-semibold text-blue-800 mb-2">Video Tutorials / वीडियो ट्यूटोरियल:</p>
          <div className="space-y-2">
            <a href="https://drive.google.com/file/d/1Z5o-Vz7MqS1rfPvZqQTWZBqeEd5jRMxt/view?usp=drive_link" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-700 hover:text-blue-900 hover:underline font-medium">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
              How to Register and Fill Parichay Sangam Form / परिचय संगम फॉर्म कैसे भरें
            </a>
            <a href="https://drive.google.com/file/d/1WqVKkTF8i8JEX6G74E8frESWi1l2CNZ2/view?usp=drive_link" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-700 hover:text-blue-900 hover:underline font-medium">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
              How to Edit Parichay Sangam Profile / परिचय संगम प्रोफ़ाइल कैसे संपादित करें
            </a>
          </div>
        </div>

        {/* Account Credentials */}
        <Card className="p-6 mb-6">
          <h3 className="font-bold text-lg text-gray-900 mb-4 border-b pb-2">Account Credentials / खाता विवरण</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm">Email / ईमेल <span className="text-destructive">*</span></Label>
              <Input type="email" value={familyForm.email} onChange={(e) => handleFamilyChange('email', e.target.value)} placeholder="your@email.com" className="mt-1" data-testid="ps-reg-email" />
            </div>
            <div>
              <Label className="text-sm">Password / पासवर्ड <span className="text-destructive">*</span></Label>
              <Input type="password" value={familyForm.password} onChange={(e) => handleFamilyChange('password', e.target.value)} placeholder="Min 6 characters" className="mt-1" data-testid="ps-reg-password" />
            </div>
            <div>
              <Label className="text-sm">Confirm Password / पासवर्ड पुष्टि <span className="text-destructive">*</span></Label>
              <Input type="password" value={familyForm.confirmPassword} onChange={(e) => handleFamilyChange('confirmPassword', e.target.value)} placeholder="Re-enter password" className="mt-1" data-testid="ps-reg-confirm-password" />
            </div>
          </div>
        </Card>

        {/* Candidates */}
        {candidates.map((candidate, idx) => (
          <CandidateCard
            key={candidate._clientId}
            initialData={candidate}
            idx={idx}
            expanded={expandedCandidates.includes(idx)}
            onToggle={toggleCandidate}
            onDataChange={handleCandidateDataChange}
            onFileChange={handleFileChange}
            onDelete={removeCandidate}
            canDelete={candidates.length > 1}
            nukhOptions={nukhOptions}
            candidateFiles={candidateFiles[idx]}
          />
        ))}

        {candidates.length < 3 && (
          <Button type="button" variant="outline" onClick={addCandidate} className="mb-8 w-full border-dashed border-2" data-testid="ps-add-candidate">
            <Plus className="w-4 h-4 mr-2" /> Add Another Candidate / एक और उम्मीदवार जोड़ें ({candidates.length}/3)
          </Button>
        )}

        {/* Guardian Details */}
        <Card className="p-6 mb-6">
          <h3 className="font-bold text-lg text-gray-900 mb-4 border-b pb-2">Parent / Guardian Details / अभिभावक विवरण</h3>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Guardian Name / अभिभावक का नाम <span className="text-destructive">*</span></Label>
                <Input value={familyForm.guardianName} onChange={(e) => handleFamilyChange('guardianName', e.target.value)} placeholder="Father / Mother / Guardian" className="mt-1" data-testid="ps-reg-guardianName" />
              </div>
              <div>
                <Label className="text-sm">Guardian WhatsApp (10 digits) <span className="text-destructive">*</span></Label>
                <Input value={familyForm.guardianWhatsapp} onChange={(e) => handleFamilyChange('guardianWhatsapp', e.target.value)} placeholder="9876543210" className="mt-1" data-testid="ps-reg-guardianWhatsapp" />
              </div>
            </div>
          </div>
        </Card>

        {/* Attending In-Person + Attendees */}
        <Card className="p-6 mb-6">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="ps-attending-toggle">
            <input type="checkbox" checked={attendingInPerson} onChange={(e) => setAttendingInPerson(e.target.checked)} className="w-5 h-5 rounded border-gray-300 text-orange-600" />
            <div>
              <p className="font-semibold text-gray-900">Would you be attending Parichay Sangam 2026 in-person?</p>
              <p className="text-sm text-gray-500">क्या आप परिचय संगम 2026 में व्यक्तिगत रूप से उपस्थित होंगे?</p>
            </div>
          </label>

          {attendingInPerson && (
            <div className="mt-6 space-y-4">
              <h4 className="font-semibold text-gray-700 border-b pb-2">Other Attendees / अन्य उपस्थित सदस्य ({attendees.length}/3)</h4>
              {attendees.map((attendee, aidx) => (
                <div key={aidx} className="bg-gray-50 p-4 rounded-lg relative" data-testid={`ps-attendee-${aidx}`}>
                  {attendees.length > 1 && (
                    <button type="button" onClick={() => removeAttendee(aidx)} className="absolute top-2 right-2 p-1.5 rounded-full text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Remove attendee" data-testid={`ps-remove-attendee-${aidx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm">Name / नाम <span className="text-destructive">*</span></Label>
                      <Input value={attendee.name} onChange={(e) => handleAttendeeChange(aidx, 'name', e.target.value)} className="mt-1" placeholder="Full name" />
                    </div>
                    <div>
                      <Label className="text-sm">Relation / संबंध <span className="text-destructive">*</span></Label>
                      <Input value={attendee.relation} onChange={(e) => handleAttendeeChange(aidx, 'relation', e.target.value)} className="mt-1" placeholder="e.g. Father, Mother" />
                    </div>
                    <div>
                      <Label className="text-sm">Phone / फ़ोन <span className="text-destructive">*</span></Label>
                      <Input value={attendee.phone} onChange={(e) => handleAttendeeChange(aidx, 'phone', e.target.value)} className="mt-1" placeholder="10 digits" />
                    </div>
                  </div>
                  {/* Attendee Photo Upload */}
                  <div className="mt-3">
                    <Label className="text-sm">Photo / फोटो <span className="text-destructive">*</span> <span className="text-xs text-gray-400">(Required for ID Card)</span></Label>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        type="button"
                        onClick={() => attendeePhotoRefs.current[aidx]?.click()}
                        className={`flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg text-sm transition-colors ${
                          attendeeFiles[aidx] ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                        }`}
                        data-testid={`ps-attendee-${aidx}-photo-upload`}
                      >
                        {attendeeFiles[aidx] ? (
                          <><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-green-700 font-medium truncate max-w-[180px]">{attendeeFiles[aidx].name}</span></>
                        ) : (
                          <><FileText className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Upload photo</span></>
                        )}
                      </button>
                      <input
                        ref={el => attendeePhotoRefs.current[aidx] = el}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => { if (e.target.files[0]) handleAttendeePhoto(aidx, e.target.files[0]); }}
                      />
                      {attendeePreviews[aidx] && (
                        <div className="flex items-center gap-2">
                          <img src={attendeePreviews[aidx]} alt="Preview" className="w-10 h-10 object-cover rounded border" />
                          <button type="button" onClick={() => removeAttendeePhoto(aidx)} className="text-xs text-red-500 hover:underline">Remove</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {attendees.length < 3 && (
                <Button type="button" variant="outline" onClick={addAttendee} className="w-full border-dashed border-2 border-orange-300 text-orange-600 hover:bg-orange-50 py-5" data-testid="ps-add-attendee-btn">
                  <Plus className="w-5 h-5 mr-2" /> Add Attendee / सदस्य जोड़ें ({attendees.length}/3)
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Fee Summary — only when attending in person */}
        {attendingInPerson && (
        <Card className="p-6 mb-6 bg-blue-50/50 border-blue-200">
          <h3 className="font-bold text-lg text-gray-900 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" /> Fee Summary / शुल्क सारांश
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-testid="ps-fee-summary">
            <div>
              <p className="text-blue-600">Candidates / उम्मीदवार</p>
              <p className="font-bold text-lg text-blue-800">{candidates.length}</p>
            </div>
            <div>
              <p className="text-blue-600">Attendees / उपस्थित</p>
              <p className="font-bold text-lg text-blue-800">{namedAttendees.length}</p>
            </div>
            <div>
              <p className="text-blue-600">Total Heads / कुल व्यक्ति</p>
              <p className="font-bold text-lg text-blue-800">{totalHeads}</p>
            </div>
            <div>
              <p className="text-blue-600">Total Fee / कुल शुल्क</p>
              <p className="font-bold text-xl text-orange-700" data-testid="ps-total-fee">{'\u20B9'}{totalFee.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-blue-500 mt-2">{'\u20B9'}1,000 per head / प्रति व्यक्ति {'\u20B9'}1,000</p>
        </Card>
        )}

        {/* Payment Section — only when attending in person */}
        {attendingInPerson && (
        <Card className="p-6 mb-8 border-2 border-orange-200 bg-orange-50/30">
          <h3 className="font-bold text-lg text-gray-900 mb-2 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-orange-600" /> Payment / भुगतान
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Amount to pay: <span className="font-bold text-orange-700 text-base">{'\u20B9'}{totalFee.toLocaleString()}</span> ({totalHeads} x {'\u20B9'}1,000)
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Payment Mode / भुगतान का तरीका <span className="text-destructive">*</span></Label>
              <div className="flex gap-3 mt-2">
                {['UPI', 'Bank Transfer'].map(mode => (
                  <button key={mode} type="button"
                    onClick={() => handleFamilyChange('paymentMode', mode)}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      familyForm.paymentMode === mode
                        ? 'border-orange-500 bg-orange-100 text-orange-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-orange-300'
                    }`}
                    data-testid={`ps-reg-payment-${mode.toLowerCase().replace(' ', '-')}`}
                  >
                    <CreditCard className="w-4 h-4 inline mr-2" />{mode}
                  </button>
                ))}
              </div>
            </div>

            {familyForm.paymentMode && (
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                {familyForm.paymentMode === 'UPI' && (
                  <div className="mb-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-3 text-center">UPI से भुगतान करें / Pay via UPI</h4>
                    <div className="flex flex-col items-center">
                      <img
                        src="https://customer-assets.emergentagent.com/job_337dba5c-8d5d-4e7d-adac-9993bcb0c3e3/artifacts/uxuej4sd_WhatsApp%20Image%202026-02-15%20at%2013.14.34.jpeg"
                        alt="UPI QR Code - Scan to Pay"
                        className="max-w-xs w-full h-auto rounded-lg shadow-md mb-3"
                      />
                      <p className="text-sm text-blue-700 font-medium">UPI ID: boim-661059350985@boi</p>
                      <p className="text-xs text-blue-600 mt-1">किसी भी UPI ऐप से स्कैन करें / Scan with any UPI App</p>
                    </div>
                  </div>
                )}
                {familyForm.paymentMode === 'Bank Transfer' && (
                  <div className="mb-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-3">बैंक विवरण / Bank Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <span className="font-medium text-green-700">खाता नाम / Account Name:</span>
                          <p className="text-gray-800">BRAHMAKSHATRIYA MARVADI KHATRI ASSOCIATION</p>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">बैंक का नाम / Bank Name:</span>
                          <p className="text-gray-800">BANK OF INDIA</p>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">खाता संख्या / Account No:</span>
                          <p className="text-gray-800 font-mono select-all">661020110000985</p>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">IFSC कोड / IFSC Code:</span>
                          <p className="text-gray-800 font-mono select-all">BKID0006610</p>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">शाखा / Branch:</span>
                          <p className="text-gray-800">JODHPUR, RAJASTHAN</p>
                        </div>
                        <div>
                          <span className="font-medium text-green-700">MICR कोड / MICR Code:</span>
                          <p className="text-gray-800 font-mono">342013002</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="text-sm">Transaction ID / UTR Number <span className="text-destructive">*</span></Label>
                  <Input value={familyForm.paymentDetails} onChange={(e) => handleFamilyChange('paymentDetails', e.target.value)} placeholder="Enter UTR / Transaction Reference Number" className="mt-1" data-testid="ps-reg-payment-details" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Payment Proof Screenshot <span className="text-destructive">*</span></Label>
              <div className="mt-1">
                <button type="button" onClick={() => paymentProofRef.current?.click()}
                  className={`flex items-center gap-2 w-full px-4 py-3 border-2 border-dashed rounded-lg transition-colors ${
                    paymentProof ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                  }`}
                  data-testid="ps-reg-payment-proof-upload"
                >
                  {paymentProof ? (
                    <><CheckCircle className="w-5 h-5 text-green-600" /><span className="text-sm text-green-700 font-medium">{paymentProof.name}</span></>
                  ) : (
                    <><FileText className="w-5 h-5 text-gray-400" /><span className="text-sm text-gray-600">Upload payment screenshot (JPG, PNG, PDF, max 5MB)</span></>
                  )}
                </button>
                <input ref={paymentProofRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                  onChange={(e) => { if (e.target.files[0]) setPaymentProof(e.target.files[0]); }}
                />
              </div>
            </div>
          </div>
        </Card>
        )}

        {/* Additional Info / Feedback */}
        <Card className="p-5 border-gray-200" data-testid="ps-additional-info-section">
          <h3 className="text-base font-bold text-gray-800 mb-1">Any Other Information / Feedback</h3>
          <p className="text-xs text-gray-500 mb-3">कोई अन्य जानकारी / सुझाव</p>
          <textarea
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder="Enter any additional information or feedback here... / कोई अन्य जानकारी या सुझाव यहाँ लिखें..."
            className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-orange-400"
            maxLength={1000}
            data-testid="ps-additional-info-input"
          />
          <p className="text-xs text-gray-400 mt-1 text-right">{additionalInfo.length}/1000</p>

          {/* Attachments */}
          <div className="mt-3 border-t pt-3">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5 mb-2">
              <Paperclip className="w-4 h-4" /> Attachments (optional) / संलग्नक
            </Label>
            <p className="text-xs text-gray-500 mb-2">Upload additional payment screenshots or any supporting documents (max 5 files, JPG/PNG/WEBP/PDF, 5MB each)</p>

            {infoAttachments.length > 0 && (
              <div className="space-y-2 mb-3">
                {infoAttachments.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md text-sm" data-testid={`info-attachment-${idx}`}>
                    <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</span>
                    <button type="button" onClick={() => setInfoAttachments(prev => prev.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {infoAttachments.length < 5 && (
              <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors text-sm text-gray-600" data-testid="info-attachment-upload-btn">
                <Plus className="w-4 h-4" /> Add Attachment
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB'); return; }
                    setInfoAttachments(prev => [...prev, file]);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </Card>

        {/* Terms & Conditions */}
        <Card className="p-5 border-orange-200 bg-orange-50/50" data-testid="ps-terms-section">
          <h3 className="text-base font-bold text-orange-800 mb-3">नियम व शर्तों का स्वीकरण</h3>
          <ul className="space-y-2.5 text-sm text-gray-700 leading-relaxed list-disc pl-5">
            <li>हमने परिचय संगम 2026 का घोषणा पत्र पढ लिया है। हम सभी प्रक्रियाओं व अनुशासन का पालन करेंगे।</li>
            <li>पंजीकरण में दी गयी जानकारियां सत्य हैं। असत्य पाये जाने पर आयोजक हमारा पंजीकरण रद्द कर सकेंगे।</li>
            <li>उम्मीदवार / उम्मीदवारों की आयु विवाह के लिये निर्धारित आयु से कम नहीं है।</li>
            <li>हमें ज्ञात है कि यह पंजीकरण सूचनाओं के आदान प्रदान के लिये है। सगाई - विवाह का निर्णय हमारा स्वयं का होगा। आयोजकों की इस संबंध में कोई जिम्मेदारी नहीं होगी।</li>
            <li>उम्मीदवार / उम्मीदवारों के विवरण ईबुक के रुप में khatrisangam.org पर प्रकाशित करने में हमारी सहमति है।</li>
            <li>किसी विवाद की स्थिति में आयोजकों का निर्णय अंतिम व मान्य होगा।</li>
            <li className="font-semibold text-orange-800">'पंजीकरण करें' बटन दबाकर हम उपर्युक्त नियमों की पालना की स्वीकृति देते हैं।</li>
          </ul>

          <div className={`mt-4 flex items-start gap-3 p-3 rounded-lg border-2 transition-colors ${termsError ? 'border-red-400 bg-red-50' : termsAccepted ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={termsAccepted}
              onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) setTermsError(false); }}
              className="mt-0.5 h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer flex-shrink-0"
              data-testid="ps-terms-checkbox"
            />
            <label htmlFor="terms-checkbox" className={`text-sm cursor-pointer select-none ${termsError ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
              मैं उपर्युक्त नियमों व शर्तों से सहमत हूँ
            </label>
          </div>
          {termsError && (
            <p className="text-red-500 text-xs mt-2 font-medium" data-testid="ps-terms-error">कृपया नियम व शर्तों को स्वीकार करें</p>
          )}
        </Card>

        <Button type="submit" disabled={submitting || !termsAccepted} className="w-full py-6 text-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50" data-testid="ps-reg-submit">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</> : 'Register / पंजीकरण करें'}
        </Button>

        <p className="text-center text-sm text-gray-500 mt-4">
          Already registered? <Link to="/parichay-sangam/login" className="text-orange-600 font-medium hover:underline">Login here</Link>
        </p>
      </form>
    </div>
  );
};

export default PSRegisterPage;
